import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import {
    Pencil,
    X,
    MapPin,
    User,
    Clock,
    CalendarCheck,
    CircleSlash,
    CircleX,
    Download,
} from 'lucide-react';

import { type IShowing } from 'src/api/landlord-showings-api';
import type { ELeadStage } from 'src/services/api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useProperties, useTouringHours } from 'src/services/api';
import { useUpdateLeadStage } from 'src/services/api/leads/use-update-lead-stage';
import { useConfirmShowing } from 'src/services/api/showings/use-confirm-showing';
import { useCancelShowing } from 'src/services/api/showings/use-cancel-showing';
import { useSetShowingStatus } from 'src/services/api/showings/use-set-showing-status';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import SelectLeadStage from 'src/components/input/select-lead-stage/SelectLeadStage';
import SelectTourStatus from 'src/pages/landlord/tours/components/calendar/SelectTourStatus';
import CancelTourConfirmModal from 'src/components/create-or-update-tour-modal/CancelTourConfirmModal';
import { CANCELED_TOUR_COLOR } from 'src/pages/landlord/tours/components/calendar/property-colors';
import { buildPropertyCalendarMaps, canEditTour } from 'src/pages/landlord/tours/components/calendar/tour-calendar-utils';
import {
    createTourIcsUrl,
    downloadUrl,
} from 'src/pages/landlord/tours/components/calendar/tour-ics';
import { formatDateTime, getEffectiveTourTimezone, getTimezoneAbbr } from 'src/utils/format-date-time';
import { CALENDAR_ACCENT } from 'src/pages/landlord/tours/components/calendar/calendar-theme';
import styles from 'src/pages/landlord/tours/components/calendar/ToursCalendar.module.css';

// Icon color for disabled (non-owner) actions, so they read as greyed-out.
const DISABLED_ICON_COLOR = '#bcc2c9';

interface Props {
    tour: IShowing | null;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onEdit: (tour: IShowing) => void;
}

/**
 * Read-only detail card anchored to a clicked tour (calendar event or lead
 * sidebar card), modeled on the Google Calendar event popup. The lead's stage
 * is editable inline here; the pencil opens the full create/update tour modal
 * for everything else.
 */
export default function TourDetailPopover({
    tour,
    anchorEl,
    onClose,
    onEdit,
}: Props) {
    const { leadsByUuid, userSettings, loggedInUser } = useAuthenticatedState();
    const { data: teamMembers = [] } = useTeamMembers({ includeInactive: true });
    const { data: properties } = useProperties();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: touringHours } = useTouringHours(tour?.property_id ?? null);
    // Prefer the timezone the tour was actually booked in (persisted on the tour),
    // falling back to the building's current timezone, then the user's. (for backward capability)
    const { timezone } = getEffectiveTourTimezone(
        tour?.timezone ?? touringHours?.timezone,
        userSettings,
    );

    const lead = tour ? leadsByUuid.get(tour.lead_uuid) : undefined;
    const { mutate: updateLeadStage } = useUpdateLeadStage(lead?.uuid ?? '');
    const { mutateAsync: confirmShowing } = useConfirmShowing();
    const { mutateAsync: cancelShowing, isLoading: isCanceling } = useCancelShowing();
    const { mutateAsync: setShowingStatus } = useSetShowingStatus();

    // Track confirm/cancel/status locally so the popover reflects the change
    // immediately, rather than reading the stale tour object the parent
    // captured when the popover opened.
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);
    const [isAttended, setIsAttended] = useState<boolean | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        if (tour) {
            setIsConfirmed(tour.is_confirmed);
            setIsCanceled(tour.is_canceled);
            setIsAttended(tour.is_attended);
        }
    }, [tour]);

    // Root-parent color, matching the event bar and the sidebar legend. Must
    // stay above the early return: hooks may not render conditionally.
    const { propertyColorById } = useMemo(
        () => buildPropertyCalendarMaps(properties ?? []),
        [properties]
    );

    if (!tour) {
        return null;
    }

    const propertyList = properties ?? [];
    const property = propertyList.find((p) => p.id === tour.property_id);


    // Leads missing from the (archived-excluding) leads list still have their
    // name embedded on the tour by the backend join, matching the calendar grid.
    const leadName = lead?.name || lead?.email || tour.lead?.name || 'Archived lead';
    const creatorName =
        teamMembers.find((m) => m.id === tour.landlord_user_id)?.name ?? 'Unassigned';

    // Only the lead owner may confirm/edit/cancel the tour, matching the
    // create/edit modal's ownership rule.
    const isOwner = tour.lead.owner === loggedInUser?.id;
    // A canceled tour is read-only even for the owner. Uses local isCanceled so an
    // in-session cancel immediately locks the remaining actions.
    const canEdit = canEditTour({ is_canceled: isCanceled }, tour.lead.owner, loggedInUser?.id);

    const goToLeadMessages = () => {
        onClose();
        const searchParams = new URLSearchParams();
        searchParams.set('lead_uuid', tour.lead_uuid);
        searchParams.set(
            'back_path',
            `${location.pathname}${location.search}`
        );
        navigate(`/landlord/inner/messages/from-lead?${searchParams.toString()}`);
    };

    // No explicit refetch here: the confirm/cancel mutations invalidate the
    // tours query prefix themselves, which refreshes every consumer (tours
    // page and lead sidebar alike).
    const handleConfirm = async () => {
        const next = !isConfirmed;
        try {
            await confirmShowing({ tourId: tour.id, value: next });
            setIsConfirmed(next);
        } catch {
            // Error toast is handled by useConfirmShowing's onError callback.
        }
    };

    const handleCancel = async () => {
        try {
            await cancelShowing({ tourId: tour.id });
            setIsCanceled(true);
            setShowCancelConfirm(false);
        } catch {
            // Error toast is handled by useCancelShowing's onError callback.
        }
    };

    // Toggle: clicking the active attendance returns the tour to not-recorded
    // (null), so a mis-click is one click to undo.
    const handleSetAttendance = async (target: boolean | null) => {
        const next = isAttended === target ? null : target;
        try {
            await setShowingStatus({ tourId: tour.id, isAttended: next });
            setIsAttended(next);
        } catch {
            // Error toast is handled by useSetShowingStatus's onError callback.
        }
    };

    const handleDownload = async () => {
        const url = await createTourIcsUrl(
            tour,
            property?.name || 'Property not set',
            leadName
        );
        downloadUrl(url, 'invite.ics');
    };

    const dateLine = `${formatDateTime(tour.start_time, 'dddd, MMMM D', '', timezone)}  ·  ${formatDateTime(tour.start_time, 'h:mm A', '', timezone)} ${getTimezoneAbbr(timezone)}`;
    const accent = propertyColorById.get(tour.property_id) ?? CANCELED_TOUR_COLOR;

    const handleStageChange = (newStage: ELeadStage) => {
        if (lead) {
            updateLeadStage({ stage: newStage });
        }
    };

    return (
        <>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                slotProps={{ paper: { className: styles.tourPopover } }}
            >
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    className={styles.popoverActions}
                >
                    <Tooltip
                        title={
                            isCanceled ?
                                'This tour is canceled' :
                                (isOwner ?
                                    (isConfirmed ? 'Unconfirm' : 'Confirm') :
                                    'Only the lead owner can confirm')
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleConfirm}
                                disabled={!canEdit}
                                className={isConfirmed && canEdit ? styles.confirmedButton : undefined}
                                aria-label={isConfirmed ? 'Unconfirm tour' : 'Confirm tour'}
                                data-testid="confirm-tour-button"
                            >
                                <CalendarCheck
                                    size={18}
                                    color={
                                        !canEdit ?
                                            DISABLED_ICON_COLOR :
                                            (isConfirmed ? '#fff' : CALENDAR_ACCENT)
                                    }
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            isCanceled ?
                                'This tour is canceled' :
                                (isOwner ? 'Edit' : 'Only the lead owner can edit')
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => onEdit(tour)}
                                disabled={!canEdit}
                                aria-label="Edit tour"
                                data-testid="edit-tour-button"
                            >
                                <Pencil size={18} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {isCanceled ? (
                        <Tooltip title="This tour is canceled">
                            <span>
                                <IconButton size="small" disabled data-testid="tour-canceled-status">
                                    <CircleSlash size={18} color={DISABLED_ICON_COLOR} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={isOwner ? 'Cancel' : 'Only the lead owner can cancel'}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => setShowCancelConfirm(true)}
                                    disabled={!isOwner}
                                    aria-label="Cancel tour"
                                    data-testid="cancel-tour-button"
                                >
                                    <CircleX
                                        size={18}
                                        color={isOwner ? '#ea4335' : DISABLED_ICON_COLOR}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title="Download invite">
                        <IconButton size="small" onClick={handleDownload} aria-label="Download invite" data-testid="download-tour-invite-button">
                            <Download size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton size="small" onClick={onClose} aria-label="Close" data-testid="close-tour-popover-button">
                            <X size={18} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Box className={styles.popoverBody}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                            className={styles.popoverAccent}
                            style={{ backgroundColor: accent }}
                        />
                        <Box className={styles.popoverHeaderText}>
                            <Tooltip title={`Tour with ${leadName}`}>
                                <Typography
                                    variant="h6"
                                    onClick={goToLeadMessages}
                                    className={`${styles.popoverTitle} ${styles.popoverTitleLink} ${isCanceled ? styles.popoverTitleCanceled : ''}`}
                                >
                                    {`Tour with ${leadName}`}
                                </Typography>
                            </Tooltip>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={styles.detailText}
                            >
                                {dateLine}
                            </Typography>
                            {!isCanceled && (
                                <Box mt={1}>
                                    <Tooltip
                                        title={canEdit ? '' : 'Only the lead owner can update attendance'}
                                    >
                                        <span>
                                            <SelectTourStatus
                                                value={isAttended}
                                                onChange={handleSetAttendance}
                                                clickable={canEdit}
                                            />
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Stack>

                    <Stack spacing={1.5} className={styles.popoverDetails}>
                        <DetailRow icon={<MapPin size={18} />}>
                            <Tooltip title={property?.name ?? 'Property'}>
                                <Typography variant="body2" className={styles.detailText}>
                                    {property?.name ?? 'Property'}
                                </Typography>
                            </Tooltip>
                        </DetailRow>

                        <DetailRow icon={<User size={18} />}>
                            <Tooltip title={creatorName}>
                                <Typography variant="body2" className={styles.detailText}>
                                    {creatorName}
                                </Typography>
                            </Tooltip>
                        </DetailRow>

                        {isConfirmed && (
                            <DetailRow icon={<Clock size={18} />} align="center">
                                <Box
                                    className={styles.confirmedBadge}
                                >
                                Confirmed
                                </Box>
                            </DetailRow>
                        )}

                        {lead?.stage && (
                            <DetailRow icon={null} align="center">
                                <SelectLeadStage
                                    value={lead.stage}
                                    onChange={handleStageChange}
                                />
                            </DetailRow>
                        )}
                    </Stack>
                </Box>
            </Popover>
            <CancelTourConfirmModal
                open={showCancelConfirm}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={handleCancel}
                isCanceling={isCanceling}
                propertyId={tour?.property_id}
            />
        </>
    );
}

function DetailRow({
    icon,
    children,
    align = 'flex-start',
}: {
    icon: React.ReactNode;
    children: React.ReactNode;
    align?: 'flex-start' | 'center';
}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems={align}>
            <Box className={styles.detailIcon}>
                {icon}
            </Box>
            {children}
        </Stack>
    );
}
